import React from "react";
import { Card } from "@UI/Components/Card/Card";
import { Text } from "@UI/Components/Text/Text";
import { Flex } from "@UI/Components/Flex/Flex";
import { Icon } from "@UI/Components/Icon/Icon";
import { VStack } from "@UI/Components/Stack/Stack";
import { FormattedMessage } from "react-intl";
import messages from "./messages";
import { PenToSquare } from "@UI/Components/Icons/PenToSquare";
import { PlusSolid } from "@UI/Components/Icons/PlusSolid";
import { Button } from "@UI/Components/Button/Button";
import { Box } from "@UI/Components/Box/Box";
import { useCompanyInformation } from "@modules/ServiceProviders/hooks/useCompanyInformation";
import { CircleExclamation } from "@UI/Components/Icons/CicleExclamation";
import { Alert, AlertIcon, AlertDescription } from "@UI/Components/Alert/Alert";
import { useNavigate } from "@Libs/Router/hooks/useNavigate";
import { useFeatureFlag } from "@Libs/FeatureFlags/hooks/useFeatureFlag";
import useMediaQuery from "@UI/hooks/useMediaQuery";

import { formatVatNumber } from "../utils/form";
import { useCurrentUser } from "@modules/Authentication/hooks/useCurrentUser";
import { IUser } from "@modules/Users/services/users";

interface ICompanyDetailsSummaryCardProps {
  isLoading?: boolean;
  nonProWarning?: boolean;
}

export const CompanyDetailsSummaryCard: React.FC<
  ICompanyDetailsSummaryCardProps
> = ({ isLoading, nonProWarning }) => {
  const { companyInformation } = useCompanyInformation();
  const { isMobile } = useMediaQuery();
  const hasCompanyInfo = !!companyInformation;
  const navigate = useNavigate();
  const { feature: proClientInfoFeatureFlag, isLoading: isLoadingFeatureFlag } =
    useFeatureFlag("pro_client_information");

  const { currentUser } = useCurrentUser();
  const vatPrefix = (currentUser as IUser)?.countryId === 2 ? "FR" : "BE";
  const proIntent = (currentUser as IUser)?.proClient;

  const onEdit = () => {
    navigate(["COMPANY_INFO"], {
      queryParams: { callback_url: window.location.href },
    });
  };

  if (
    !proIntent ||
    isLoading ||
    isLoadingFeatureFlag ||
    !proClientInfoFeatureFlag?.enabled
  ) {
    // Could add a skeleton here
    return null;
  }

  const streetLine = [companyInformation?.address, companyInformation?.postcode]
    .filter(Boolean)
    .join(", ");

  const wrapInCard = (children) => {
    if (isMobile) {
      return (
        <Card
          mt="5"
          shadow="lg"
          bg="white"
          p={5}
          borderRadius="xl"
          alignContent="center"
        >
          {children}
        </Card>
      );
    }

    return children;
  };

  return wrapInCard(
    <VStack spacing={4} alignItems="flex-start" w="full">
      <Card
        padding={4}
        bg={"sand.100"}
        borderWidth={1}
        borderColor="transparent"
        boxShadow="none"
        width="full"
      >
        <Flex justifyContent="space-between" alignItems="center">
          <VStack spacing={1} alignItems="flex-start" w="full">
            <Text fontSize="sm" mb={1}>
              <FormattedMessage {...messages.companyInfoTitle} />
            </Text>

            {hasCompanyInfo ? (
              <VStack spacing={1} alignItems="flex-start" color="gray.800">
                <Text fontWeight="bold" mb={0}>
                  {formatVatNumber(
                    companyInformation?.vat_number || "",
                    vatPrefix
                  )}
                </Text>
                <Text fontWeight="bold" mb={0}>
                  {companyInformation?.company_name}
                </Text>
                <Text fontWeight="bold" mb={0}>
                  {streetLine}
                </Text>
                <Text fontWeight="bold" mb={0}>
                  {companyInformation?.city}
                </Text>
              </VStack>
            ) : (
              <Button
                variant="ghost"
                color="pink.500"
                leftIcon={<Icon as={PlusSolid} />}
                onClick={onEdit}
                justifyContent="start"
                px={0}
                _hover={{ bg: "transparent" }}
              >
                <FormattedMessage {...messages.companyInfoProvideDetails} />
              </Button>
            )}
          </VStack>

          {hasCompanyInfo && (
            <Button
              variant="ghost"
              onClick={onEdit}
              p={0}
              minW="auto"
              height="auto"
            >
              <Icon as={PenToSquare} color="red.500" boxSize={5} />
            </Button>
          )}
        </Flex>
      </Card>

      {nonProWarning && (
        <Box mt={4}>
          <Alert status="warning" borderRadius="md">
            <AlertIcon as={CircleExclamation} />
            <AlertDescription>
              <FormattedMessage {...messages.nonProWarning} />
            </AlertDescription>
          </Alert>
        </Box>
      )}
    </VStack>
  );
};
